/**
 * product-media-gallery.js — <product-media-gallery>, desktop thumbnail rail.
 *
 * Progressive enhancement for the PDP media strip (sections/product.liquid).
 * The strip itself is native scroll-snap markup that already works without JS
 * (swipe on mobile, scrollbar on desktop); from 990px it shows one slide at a
 * time and this element wires the thumbnail buttons to it:
 *
 *  - clicking a thumbnail scrolls the strip to that slide (instant — no
 *    animation) and leaves focus on the clicked thumbnail;
 *  - the strip's visible slide is tracked with an IntersectionObserver
 *    (no scroll listener), keeping the thumbnails' aria-current in sync when
 *    the strip is scrolled by touch, trackpad or scrollbar;
 *  - the active thumbnail is kept in view inside a scrollable rail.
 *
 * Self-contained (no imports) and separate from product-form.js: media and
 * purchasing are different concerns. Loaded from the product section only
 * when a product has more than one media item.
 */

class ProductMediaGallery extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-media-list]');
    this.thumbs = Array.from(this.querySelectorAll('[data-media-thumb]'));
    if (!this.list || this.thumbs.length === 0) return;

    this.slides = this.thumbs
      .map((thumb) => document.getElementById(thumb.dataset.target))
      .filter(Boolean);
    if (this.slides.length !== this.thumbs.length) return;

    this.rail = this.thumbs[0].closest('.product__thumbs');

    this.thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => this.#show(index));
    });

    this.observer = new IntersectionObserver(this.#onVisible, {
      root: this.list,
      threshold: 0.6,
    });
    this.slides.forEach((slide) => this.observer.observe(slide));

    // Marks the enhancement as live (CSS hides the strip's scrollbar and
    // shows the rail only once this is set).
    this.setAttribute('data-gallery-ready', '');
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  #show(index) {
    const slide = this.slides[index];
    if (!slide) return;
    this.list.scrollTo({ left: slide.offsetLeft, behavior: 'auto' });
    this.#setActive(index);
  }

  #onVisible = (entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const index = this.slides.indexOf(entry.target);
      if (index !== -1) this.#setActive(index);
    });
  };

  #setActive(index) {
    this.thumbs.forEach((thumb, i) => {
      if (i === index) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
    this.#keepThumbInView(this.thumbs[index]);
  }

  // Scrolls only the rail (never the page) when the active thumbnail is
  // outside it.
  #keepThumbInView(thumb) {
    const rail = this.rail;
    if (!rail || rail.scrollHeight <= rail.clientHeight) return;
    const top = thumb.offsetTop; // rail is position: relative
    const bottom = top + thumb.offsetHeight;
    if (top < rail.scrollTop) {
      rail.scrollTop = top;
    } else if (bottom > rail.scrollTop + rail.clientHeight) {
      rail.scrollTop = bottom - rail.clientHeight;
    }
  }
}

customElements.define('product-media-gallery', ProductMediaGallery);
